<template>
  <form id="groupForm" name="groupForm">
    <div class="row">
      <div class="col">
        <div class="mb-2">
          <label for="groupTitle" class="form-label">{{ t('GROUP_TITLE') }}*</label>
          <InputText
              id="groupTitle"
              name="groupTitle"
              v-model="groupForm.title"
              required
              type="text"
              class="w-100"
          />
        </div>

        <div class="mb-2">
          <label for="groupSize" class="form-label">{{ t('GROUP_SIZE') }}*</label>
          <InputNumber
              required
              :showButtons="true"
              v-model="groupForm.groupSize"
              id="groupSize"
              name="groupSize"
              min="1"
              inputClass="w-100"
              class="d-block"
          />
        </div>

        <div v-if="module" class="mb-2">
          <label for="moduleTrainers" class="form-label">{{ t('TRAINER') }}</label>
          <MultiSelect
              input-id="moduleTrainers"
              v-model="groupForm.trainers"
              :options="trainers"
              optionLabel="fullName"
              dataKey="id"
              :placeholder="`${t('SEARCH')}...`"
              :showToggleAll="false"
              filter
              class="w-100"
          />
        </div>
      </div>

      <div class="col">
        <div class="mb-2">
          <label for="location" class="form-label">{{ t('LOCATION') }}*</label>
          <Select
              v-model="groupForm.location"
              required
              label-id="location"
              name="location"
              :options="getGroupLocationOptions(t)"
              option-label="text"
              option-value="value"
              :placeholder="t('SELECT')"
              class="w-100"
          />
        </div>

        <div v-if="groupForm.location !== GroupLocation.ONLINE" class="mb-2">
          <label for="address" class="form-label">{{ t('ADDRESS') }}</label>
          <InputText
              v-model="groupForm.address"
              type="text"
              id="address"
              name="address"
              class="w-100"
          />
        </div>

        <div class="mb-2">
          <label for="language" class="form-label">{{ t('LANGUAGE') }}*</label>
          <Select
              v-model="groupForm.language"
              required
              label-id="language"
              name="language"
              :options="getGroupLangOptions(t)"
              option-label="text"
              option-value="value"
              :placeholder="t('SELECT')"
              class="w-100"
          />
        </div>

        <div class="mb-2">
          <label for="groupStatus" class="form-label">{{ t('STATUS') }}*</label>
          <Select
              :disabled="groupForm.location === GroupLocation.IN_HOUSE"
              v-model="groupForm.status"
              required
              label-id="groupStatus"
              name="groupStatus"
              :options="statusOptions"
              option-label="text"
              option-value="value"
              :placeholder="t('SELECT')"
              class="w-100"
          />
        </div>
      </div>
    </div>

    <div class="row my-3 py-3 border-bottom border-top">
      <div class="col">
        <div class="mb-2">
          <label for="startDate" class="form-label">{{ t(module?.type === ModuleType.RECURRING ? 'START_DATE' : 'DATE') }}*</label>
          <DatePicker
              v-model="groupForm.startDate"
              input-id="startDate"
              required
              :min-date="new Date()"
              :placeholder="t('SELECT_DATE')"
              date-format="dd.mm.yy"
              class="w-100"
          />
        </div>
      </div>

      <div v-if="module?.type === ModuleType.RECURRING" class="col">
        <div class="mb-2">
          <label for="endDate" class="form-label">{{ t('END_DATE') }}*</label>
          <DatePicker
              v-model="groupForm.endDate"
              input-id="endDate"
              required
              :disabled="!groupForm.startDate"
              :min-date="groupForm.startDate"
              :placeholder="t('SELECT_DATE')"
              date-format="dd.mm.yy"
              class="w-100"
          />
        </div>
      </div>
    </div>

    <div class="row h5 mb-3 pb-3">
      <div class="col">
        <span>{{ t("TOR") }}</span>
        <Button
            v-if="module?.type === ModuleType.RECURRING && eventTimes.length < 100"
            v-tooltip="t('ADD_NEW')"
            icon="pi pi-plus"
            text
            rounded
            class="ms-3 p-0 mb-1 add-event-time-btn"
            @click="addEventTime"
        />
      </div>
    </div>

    <div v-for="(eventTime, index) in eventTimes" :key="index" class="row align-items-end">
      <div v-if="module?.type === ModuleType.RECURRING" class="col-xl-6">
        <div class="mb-2">
          <label :for="`weekday-select-${index}`" class="form-label">{{ t('WEEKDAY') }}*</label>
          <MultiSelect
              :input-id="`weekday-select-${index}`"
              v-model="eventTime.weekdays"
              :options="weekDays"
              optionLabel="text"
              optionValue="value"
              :placeholder="t('SELECT')"
              :showToggleAll="false"
              filter
              class="w-100"
          />
        </div>
      </div>

      <div class="col">
        <div class="mb-2">
          <label :for="`start-time-select-${index}`" class="form-label">{{ t('START') }}*</label>
          <DatePicker
              :input-id="`start-time-select-${index}`"
              v-model="eventTime.startTime"
              timeOnly
              showIcon
              class="w-100"
          />
        </div>
      </div>

      <div class="col">
        <div class="mb-2">
          <label :for="`end-time-select-${index}`" class="form-label">{{ t('END') }}*</label>
          <DatePicker
              :input-id="`end-time-select-${index}`"
              v-model="eventTime.endTime"
              timeOnly
              showIcon
              class="w-100"
          />
        </div>
      </div>

      <div class="col col-auto" v-if="eventTimes.length > 1">
        <div class="mb-2">
          <Button
              v-tooltip="t('REMOVE')"
              icon="pi pi-times"
              severity="danger"
              text
              rounded
              class="mb-1 px-1 remove-event-time-btn"
              @click="removeEventTime(index)"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import {computed, ref, toRefs, watch} from "vue";
import {useI18n} from "vue-i18n";
import moment from "moment-timezone";
import {easter} from "date-easter";
import {getGroupLangOptions, getGroupLocationOptions} from "@/model/group";
import {ModuleType} from "@/model/module-type";
import {GroupStatus} from "@/model/group-status";
import {GroupLocation} from "@/model/group-location";
import {useToast} from "@/util/toastUtils";

const props = defineProps({module: Object, trainers: Array});

defineExpose({submitForm});

const {module, trainers} = toRefs(props);
const {t} = useI18n();
const toast = useToast();
const emit = defineEmits(['submit-group'])

const groupForm = ref({
  title: '',
  groupSize: null,
  trainers: [],
  trainee: null,
  numberOfTrainees: 0,
  location: '',
  address: '',
  language: '',
  status: '',
  startDate: null,
  endDate: null,
});

const eventTimes = ref([{
  weekdays: [],
  startTime: "",
  endTime: "",
}]);

const weekDays = computed(() => {
  return [
    {value: 1, text: t("MONDAY")},
    {value: 2, text: t("TUESDAY")},
    {value: 3, text: t("WEDNESDAY")},
    {value: 4, text: t("THURSDAY")},
    {value: 5, text: t("FRIDAY")},
    {value: 6, text: t("SATURDAY")},
    {value: 7, text: t("SUNDAY")},
  ]
})

const statusOptions = [
  {value: "ARCHIVED", text: t("ARCHIVED")},
  {value: "DRAFT", text: t("DRAFT")},
  {value: "PRIVATE", text: t("PRIVATE")},
  {value: "PUBLIC", text: t("PUBLIC")},
];

function submitForm() {
  if (isFormValid()) {
    emit('submit-group', {
      ...groupForm.value,
      trainingEvents: getTrainingEvents(),
      status: getGroupStatus(),
      address: getGroupAddress(),
    });
  }
}

function isFormValid() {
  const form = document.getElementById("groupForm");
  const formValid = form.reportValidity();

  if (!formValid) {
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return false;
  }

  if (!groupForm.value.startDate || (props.module.type === ModuleType.RECURRING && !groupForm.value.endDate)) {
    toast.makeErrorToast(t("EMPTY_FIELDS"));
    return false;
  }

  if (props.module.type === ModuleType.RECURRING) {
    if (moment(groupForm.value.endDate).isBefore(moment(groupForm.value.startDate))) {
      toast.makeErrorToast(t("DATE_MISMATCH"));
      return false;
    }
  }

  if (!eventTimes.value.length) {
    toast.makeErrorToast(t("DAYS_MISSING"));
    return false;
  }

  for (const eventTime of eventTimes.value) {
    if (props.module.type === ModuleType.RECURRING && !eventTime.weekdays?.length) {
      toast.makeErrorToast(t("EMPTY_FIELDS"));
      return false;
    }

    if (!eventTime.startTime || !eventTime.endTime) {
      toast.makeErrorToast(t("EMPTY_FIELDS"));
      return false;
    }

    const timeStart = moment(eventTime.startTime, "HH:mm");
    const timeEnd = moment(eventTime.endTime, "HH:mm");

    if (timeEnd.isBefore(timeStart)) {
      toast.makeErrorToast(t("TIME_MISMATCH"));
      return false;
    }
  }

  return true;
}

function getTrainingEvent(day, eventTime) {
  const timeStart = moment(eventTime.startTime, "HH:mm");
  const timeEnd = moment(eventTime.endTime, "HH:mm");

  const startDate = moment(day).set({
    hour: timeStart.get("hour"),
    minute: timeStart.get("minute"),
  });

  const endDate = moment(day).set({
    hour: timeEnd.get("hour"),
    minute: timeEnd.get("minute"),
  });

  return {
    startTime: startDate.toISOString(),
    endTime: endDate.toISOString(),
  };
}

function getTrainingEvents() {
  if (props.module.type === ModuleType.RECURRING) {
    const recurringEvents = [];

    for (const trainingDate of getTrainingDates(groupForm.value.startDate, groupForm.value.endDate)) {
      for (const eventTime of eventTimes.value) {
        if (eventTime.weekdays?.includes(trainingDate.isoWeekday())) {
          recurringEvents.push(getTrainingEvent(trainingDate, eventTime));
        }
      }
    }

    return recurringEvents;
  }

  return eventTimes.value.map(eventTime => getTrainingEvent(moment(groupForm.value.startDate), eventTime));
}

function getGroupStatus() {
  return groupForm.value.location === GroupLocation.IN_HOUSE ? GroupStatus.PRIVATE : groupForm.value.status;
}

function getGroupAddress() {
  return groupForm.value.location === GroupLocation.ONLINE ? null : groupForm.value.address;
}

function getTrainingDates(start, end) {
  const dateFormat = "DD.MM.YYYY";
  const trainingDates = [];
  const holidays = new Set(getHolidays(start, end).map(holiday => holiday.format(dateFormat)));
  const weekdays = new Set(eventTimes.value.flatMap((et) => et.weekdays || []));

  start = moment(start);
  end = moment(end);

  while (start <= end) {
    if (!holidays.has(start.format(dateFormat)) && weekdays.has(start.isoWeekday())) {
      trainingDates.push(moment(start));
    }

    start = start.add(1, "days");
  }

  return trainingDates;
}

function getHolidays(start, end) {
  const holidays = [];

  for (let year = start.getFullYear(); year <= end.getFullYear(); year++) {
    const easterDate = moment(easter(year).toString());
    holidays.push(easterDate);

    const goodFriday = moment(easterDate).subtract(2, "days");
    holidays.push(goodFriday);

    const pentecost = moment(easterDate).add(49, "days");
    holidays.push(
      pentecost, 
      moment({year: year, month: 0, day: 1}),
      moment({year: year, month: 1, day: 24}),
      moment({year: year, month: 4, day: 1}),
      moment({year: year, month: 5, day: 23}),
      moment({year: year, month: 5, day: 24}),
      moment({year: year, month: 7, day: 20}),
      moment({year: year, month: 11, day: 24}),
      moment({year: year, month: 11, day: 25}),
      moment({year: year, month: 11, day: 26}),
    );
  }

  return holidays;
}

const addEventTime = () => {
  eventTimes.value.push({
    weekdays: [],
    startTime: "",
    endTime: "",
  });
};

const removeEventTime = (index) => {
  eventTimes.value = eventTimes.value.filter((_, i) => i !== index);
};

watch(groupForm.value, () => {
  if (groupForm.value.location === GroupLocation.IN_HOUSE) {
    groupForm.value.status = GroupStatus.PRIVATE;
  }
});
</script>
